import {
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from "@tanstack/react-router";
import { Route as rootRoute } from "./__root";
import { createAuthzRoutes } from "./domains/authz";
import { createCoreRoutes } from "./domains/core";
import { createFeedbackRoutes } from "./domains/feedback";
import { createFinanceRoutes } from "./domains/finance";
import { createMarketingRoutes } from "./domains/marketing";
import { createMenuRoutes } from "./domains/menu";
import { createMonitorRoutes } from "./domains/monitor";
import { createOrganizationRoutes } from "./domains/organization";
import { createSettingsRoutes } from "./domains/settings";
import { createUserRoutes } from "./domains/user";

// Auth Layout
const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "auth-layout",
  component: lazyRouteComponent(() => import("layouts/auth")),
});

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/auth/login",
  component: lazyRouteComponent(() => import("pages/auth/login")),
});

// Landing page for the invite link mailed to a new staff member: the link token and the
// one-time password from the same email are spent here on a password the staff member
// chooses. Unauthenticated by necessity — the account cannot log in until this has
// happened, so it sits under the auth layout rather than the dashboard one.
const verifyEmailRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/auth/verify-email",
  component: lazyRouteComponent(() => import("pages/auth/verify_email")),
});

// Mail a code, then spend it on a new password. Also the way back in once the 90-day
// password limit expires, which is why it is reachable from the login page rather than
// only from an admin.
const forgotPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/auth/forgot-password",
  component: lazyRouteComponent(() => import("pages/auth/forgot_password")),
});

// Where IMS sends the browser back after sign-in. Public: the session does not exist
// until this page has spent the authorization code.
const ssoCallbackRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/auth/sso/callback",
  component: lazyRouteComponent(() => import("pages/auth/sso_callback")),
});

// Dashboard Layout
const dashboardLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "dashboard-layout",
  beforeLoad: ({ context }) => {
    if (!context.authorized) {
      throw redirect({ to: "/auth/login" });
    }
  },
  component: lazyRouteComponent(() => import("layouts/dashboard")),
});

// Dashboard Index
const dashboardIndexRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/dashboard",
  component: lazyRouteComponent(() => import("pages/dashboard")),
});

// File Management
const fileManagementRoute = createRoute({
  getParentRoute: () => dashboardLayoutRoute,
  path: "/file_management",
  component: lazyRouteComponent(() => import("pages/file_manage")),
});

// Exception Routes
const notAuthorizedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/403",
  component: lazyRouteComponent(() => import("pages/exceptions/NotAuthorized")),
});

const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "*",
  component: lazyRouteComponent(() => import("pages/exceptions/NotFound")),
});

// Index redirect
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: ({ context }) => {
    if (context.authorized) {
      throw redirect({ to: "/dashboard" });
    } else {
      throw redirect({ to: "/auth/login" });
    }
  },
});

// Route Tree
const routeTree = rootRoute.addChildren([
  indexRoute,
  authLayoutRoute.addChildren([
    loginRoute,
    verifyEmailRoute,
    forgotPasswordRoute,
    ssoCallbackRoute,
  ]),
  dashboardLayoutRoute.addChildren([
    dashboardIndexRoute,
    ...createSettingsRoutes(dashboardLayoutRoute),
    ...createFinanceRoutes(dashboardLayoutRoute),
    ...createMarketingRoutes(dashboardLayoutRoute),
    ...createFeedbackRoutes(dashboardLayoutRoute),
    ...createCoreRoutes(dashboardLayoutRoute),
    ...createMenuRoutes(dashboardLayoutRoute),
    ...createUserRoutes(dashboardLayoutRoute),
    ...createOrganizationRoutes(dashboardLayoutRoute),
    ...createAuthzRoutes(dashboardLayoutRoute),
    ...createMonitorRoutes(dashboardLayoutRoute),
    fileManagementRoute,
  ]),
  notAuthorizedRoute,
  notFoundRoute,
]);

// Create Router
export const router = createRouter({
  routeTree,
  context: {
    authorized: false,
  },
  defaultPreload: "intent",
});

// Type Registration
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
